import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { DataApiInputParams } from 'src/types/api';
import { Builder, appendFormData, getHeaders, getQueryString } from './utils';

type ICreateTenantProps = {
  domain?: string;
  plan: string;
  credits_per_month: number;
  is_block: boolean;
  license_id: number;
  expiry_on: string;
  email: string;
  max_users: number;
  file_upload_limit_per_transaction: number;
  language: string;
  max_search_plus: number;
  max_doc_analyst_analysis: number;
  max_doc_analyst_question_per_analysis: number;
  max_ocr_files: number;
  max_smart_meet_duration_secs: number;
  max_data_analyst_query: number;
  workflow_runs: number;
  workflow_credits: number;
};

type IDeleteTenantProps = {
  tenant_id: number;
  realm_name: string;
};

type IEmailTenantProps = {
  email: string;
  username: string;
};

export default (builder: Builder) => ({
  getTenants: builder.query({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      const url = `tenant/?${getQueryString(arg.params)}`;

      return fetchWithBQ({
        url: url,
        ...getHeaders(url),
        responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
      });
    },
    providesTags: ['Tenants']
  }),
  reprocessTenantFiles: builder.mutation({
    async queryFn({ params }, { getState }, extraOptions, baseQuery) {
      let result: any = await baseQuery({
        url: `files/file`,
        method: 'PATCH',
        body: params.formData,
        ...getHeaders(`files/file`, params.formData)
      });
      if (result.error) {
        return result;
      }
      return { data: result || { noFilesToProcess: result.msg } };
    }
  }),
  updateTenantPlan: builder.mutation({
    async queryFn({ params }, { getState }, extraOptions, baseQuery) {
      let result: any = await baseQuery({
        url: `tenant/`,
        method: 'PATCH',
        body: params.formData,
        ...getHeaders(`tenant/`, params.formData)
      });
      if (result.error) {
        return result;
      }
      return { data: result || { noFilesToProcess: result.msg } };
    },
    invalidatesTags: ['Tenants']
  }),
  updateOrgLogo: builder.mutation({
    async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
      let formData = arg.params;
      let url = `tenant/logo`;
      return fetchWithBQ({
        url: url,
        method: 'PATCH',
        body: formData,
        ...getHeaders(url, formData),
        responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
      });
    },
    invalidatesTags: ['Icon']
  }),
  deleteLogo: builder.mutation({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let formData = appendFormData(arg.params);
      let url = `tenant/logo`;
      return fetchWithBQ({
        url: url,
        method: 'DELETE',
        body: formData,
        ...getHeaders(url, formData),
        responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
      });
    },
    invalidatesTags: ['Icon']
  }),
  createTenant: builder.mutation<unknown, ICreateTenantProps>({
    async queryFn(args, _, __, fetchWithBQ) {
      const url = `tenant/`;
      const formData = appendFormData(args);
      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['Tenants']
  }),
  deleteTenant: builder.mutation<unknown, IDeleteTenantProps>({
    async queryFn(args, _, __, fetchWithBQ) {
      const url = `tenant/`;
      const formData = appendFormData(args);
      return fetchWithBQ({
        url: url,
        method: 'DELETE',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['Tenants']
  }),
  getServiceClient: builder.query({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let url = `fw/service-client-config?${getQueryString(arg.params)}`;
      return fetchWithBQ({
        url: url,
        ...getHeaders(url)
      });
    },
    providesTags: ['ServiceClient']
  }),
  createServiceClient: builder.mutation({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let formData = appendFormData(arg.params);
      let url = `fw/service-client-config`;
      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['ServiceClient']
  }),
  updateServiceClient: builder.mutation({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let formData = appendFormData(arg.params);
      let url = `fw/service-client-config`;
      return fetchWithBQ({
        url: url,
        method: 'PATCH',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['ServiceClient']
  }),
  createCompanyInfo: builder.mutation({
    async queryFn(
      arg: DataApiInputParams,
      { getState },
      extraOptions,
      fetchWithBQ
    ) {
      let formData = appendFormData(arg.params);
      let url = `tenant/company-info`;
      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['UserPreferences']
  }),
  resendTenantUserCreationEmail: builder.mutation<unknown, IEmailTenantProps>({
    async queryFn(args, _, __, fetchWithBQ) {
      const url = `tenant/resend-email`;
      const formData = appendFormData(args);
      return fetchWithBQ({
        url: url,
        method: 'POST',
        body: formData,
        ...getHeaders(url, formData)
      });
    },
    invalidatesTags: ['Tenants']
  })
});
